import React, { useState } from 'react';
import {
    AppBar, Box, Button, Chip, Container, Dialog, Divider,
    Grid, IconButton, List, ListItem, ListItemButton, ListItemText,
    Paper, Typography, CircularProgress, Tooltip, Alert, Snackbar,
    DialogContent
} from "@mui/material";
import {
    Search, RemoveRedEye, ContentCopy, LocationOn,
    Phone, Work, Group, Person, Close as CloseIcon,
    Info, MyLocation
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ExclusiveDatagrid from '../../../../components/ui/ExclusiveDatagrid';
import Toolbar from '@mui/material/Toolbar';
import { GridActionsCellItem } from '@mui/x-data-grid';
import LocationSearch from './LocationSearch';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(1),
            borderRadius: theme.spacing(1),
        }
    }
}));

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        transform: 'translateX(4px)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
    }
}));

const DetailSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .section-title': {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
            color: theme.palette.primary.main,
        }
    }
}));

const getZodiacSign = (birthDate) => {
    if (!birthDate) return '';
    
    // Tarihi parçalara ayır
    const [day, month] = birthDate.split('.').map(Number);
    
    // Burç hesaplama
    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'Koç';
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'Boğa';
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'İkizler';
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'Yengeç';
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'Aslan';
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'Başak';
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'Terazi';
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'Akrep';
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'Yay';
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return 'Oğlak';
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'Kova';
    if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return 'Balık';
    
    return '';
};

const DataModal = ({ 
    open, 
    onClose, 
    target, 
    parents, 
    childs, 
    numbers, 
    locations, 
    jobs, 
    jobsauthorities, 
    siblings, 
    relatives, 
    showRelatives, 
    loading,
    onLoadRelatives,
    onTargetAim,
    onJobDetails,
    onCopyToClipboard,
    onOpenInGoogleMaps,
    onShowNotification
}) => {
    const [showMap, setShowMap] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);

    const handleShowMap = (address) => {
        setSelectedAddress(address);
        setShowMap(true);
    };

    const handleCloseMap = () => {
        setShowMap(false);
        setSelectedAddress(null);
    };

    return (
        <>
            <StyledDialog
                fullScreen
                open={open}
                onClose={onClose}
            >
                <AppBar position="relative" elevation={0} sx={{ backgroundColor: 'background.paper' }}>
                    <Toolbar>
                        <IconButton edge="start" color="primary" onClick={onClose} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1, color: 'text.primary' }} variant="h6">
                            {target?.TC} - {target?.ADI} {target?.SOYADI}
                        </Typography>
                    </Toolbar>
                </AppBar>

                <Container maxWidth="xl" sx={{ py: 4 }}>
                    <Grid container spacing={3}>
                        {/* Personal Information Section */}
                        <Grid item xs={12} md={6}>
                            <DetailSection>
                                <Box className="section-title">
                                    <Person />
                                    <Typography variant="h6">Kişisel Bilgiler</Typography>
                                </Box>
                                <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                    <List>
                                        {[
                                            { label: 'TC', value: target?.TC },
                                            { label: 'ADI', value: target?.ADI },
                                            { label: 'SOYADI', value: target?.SOYADI },
                                            { 
                                                label: 'DOGUMTARIHI', 
                                                value: target?.DOGUMTARIHI,
                                                secondary: getZodiacSign(target?.DOGUMTARIHI) ? `Burç: ${getZodiacSign(target?.DOGUMTARIHI)}` : undefined
                                            },
                                            { label: 'DOĞUM YERİ', value: locations?.[0]?.DogumYeri },
                                            { label: 'UYRUK', value: target?.UYRUK }
                                        ].map((item, index) => (
                                            <StyledListItem 
                                                key={index}
                                                onClick={() => onCopyToClipboard(item.value, item.label)}
                                            >
                                                <ListItemText
                                                    primary={item.label}
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="body1" color="textPrimary">
                                                                {item.value}
                                                            </Typography>
                                                            {item.secondary && (
                                                                <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                                                                    {item.secondary}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    primaryTypographyProps={{
                                                        variant: 'subtitle2',
                                                        color: 'textSecondary'
                                                    }}
                                                />
                                                <ContentCopy color="action" fontSize="small" />
                                            </StyledListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </DetailSection>

                            {/* Family Information Section */}
                            <DetailSection>
                                <Box className="section-title">
                                    <Group />
                                    <Typography variant="h6">Aile Bilgileri</Typography>
                                </Box>
                                <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                    <List>
                                        {/* Parents */}
                                        {parents?.map((parent, index) => (
                                            <StyledListItem 
                                                key={`parent-${index}`}
                                                onClick={() => onTargetAim(parent.TC)}
                                            >
                                                <ListItemText 
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Chip 
                                                                label={parent.TC === target?.ANNETC ? 'ANNE' : 'BABA'}
                                                                size="small"
                                                                color="primary"
                                                                sx={{ 
                                                                    borderRadius: '4px',
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                            <Typography variant="body1" color="textPrimary">
                                                                {parent.ADI} {parent.SOYADI}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                                            TC: {parent.TC}
                                                        </Typography>
                                                    }
                                                />
                                                <ContentCopy 
                                                    color="action" 
                                                    fontSize="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onCopyToClipboard(parent.TC, 'TC');
                                                    }}
                                                />
                                            </StyledListItem>
                                        ))}

                                        {/* Siblings */}
                                        {siblings?.map((sibling, index) => (
                                            <StyledListItem 
                                                key={`sibling-${index}`}
                                                onClick={() => onTargetAim(sibling.TC)}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Chip 
                                                                label="KARDEŞ"
                                                                size="small"
                                                                color="secondary"
                                                                sx={{ 
                                                                    borderRadius: '4px',
                                                                    fontWeight: 500,
                                                                    backgroundColor: '#4caf50'
                                                                }}
                                                            />
                                                            <Typography variant="body1" color="textPrimary">
                                                                {sibling.ADI} {sibling.SOYADI}
                                                            </Typography>
                                                            <Chip 
                                                                label={sibling.siblingType}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ 
                                                                    borderRadius: '4px',
                                                                    fontWeight: 500,
                                                                    ml: 1
                                                                }}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                                            TC: {sibling.TC}
                                                        </Typography>
                                                    }
                                                />
                                                <ContentCopy 
                                                    color="action" 
                                                    fontSize="small" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onCopyToClipboard(sibling.TC, 'TC');
                                                    }}
                                                />
                                            </StyledListItem>
                                        ))}

                                        {/* Children */}
                                        {childs?.map((child, index) => (
                                            <StyledListItem 
                                                key={`child-${index}`}
                                                onClick={() => onTargetAim(child.TC)}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Chip 
                                                                label="ÇOCUK"
                                                                size="small"
                                                                color="secondary"
                                                                sx={{ 
                                                                    borderRadius: '4px',
                                                                    fontWeight: 500,
                                                                    backgroundColor: '#9c27b0'
                                                                }}
                                                            />
                                                            <Typography variant="body1" color="textPrimary">
                                                                {child.ADI} {child.SOYADI}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                                            TC: {child.TC}
                                                        </Typography>
                                                    }
                                                />
                                                <ContentCopy 
                                                    color="action" 
                                                    fontSize="small" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onCopyToClipboard(child.TC, 'TC');
                                                    }}
                                                />
                                            </StyledListItem>
                                        ))}

                                        {(!parents || parents.length === 0) && 
                                         (!siblings || siblings.length === 0) && 
                                         (!childs || childs.length === 0) && (
                                            <ListItem>
                                                <ListItemText 
                                                    primary="Aile bilgisi bulunamadı"
                                                    primaryTypographyProps={{
                                                        color: 'text.secondary',
                                                        align: 'center'
                                                    }}
                                                />
                                            </ListItem>
                                        )}
                                    </List>
                                </Paper>
                            </DetailSection>

                            {/* Relatives Section */}
                            <DetailSection>
                                <Box className="section-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Group />
                                        <Typography variant="h6">Akrabalar</Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={onLoadRelatives}
                                        disabled={loading}
                                        startIcon={loading ? <CircularProgress size={20} /> : <Group />}
                                    >
                                        {loading ? 'Yükleniyor...' : 'Akrabaları Göster'}
                                    </Button>
                                </Box>
                                {showRelatives && (
                                    <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                        <List>
                                            {relatives?.map((cousin, index) => (
                                                <StyledListItem 
                                                    key={`cousin-${index}`}
                                                    onClick={() => onTargetAim(cousin.TC)}
                                                >
                                                    <ListItemText 
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Chip 
                                                                    label="KUZEN"
                                                                    size="small"
                                                                    color="secondary"
                                                                    sx={{ 
                                                                        borderRadius: '4px',
                                                                        fontWeight: 500,
                                                                        backgroundColor: '#ff9800'
                                                                    }}
                                                                />
                                                                <Typography variant="body1" color="textPrimary">
                                                                    {cousin.ADI} {cousin.SOYADI}
                                                                </Typography>
                                                                <Chip 
                                                                    label={cousin.cousinType}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ 
                                                                        borderRadius: '4px',
                                                                        fontWeight: 500,
                                                                        ml: 1
                                                                    }}
                                                                />
                                                                <Chip 
                                                                    label={cousin.parentRelation}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ 
                                                                        borderRadius: '4px',
                                                                        fontWeight: 500,
                                                                        ml: 1
                                                                    }}
                                                                />
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                                                TC: {cousin.TC}
                                                            </Typography>
                                                        }
                                                    />
                                                    <ContentCopy 
                                                        color="action" 
                                                        fontSize="small" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onCopyToClipboard(cousin.TC, 'TC');
                                                        }}
                                                    />
                                                </StyledListItem>
                                            ))}
                                            {(!relatives || relatives.length === 0) && (
                                                <ListItem>
                                                    <ListItemText 
                                                        primary="Akraba bilgisi bulunamadı"
                                                        primaryTypographyProps={{
                                                            color: 'text.secondary',
                                                            align: 'center'
                                                        }}
                                                    />
                                                </ListItem>
                                            )}
                                        </List>
                                    </Paper>
                                )}
                            </DetailSection>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            {/* Location Information */}
                            <DetailSection>
                                <Box className="section-title">
                                    <LocationOn />
                                    <Typography variant="h6">Adres Bilgileri</Typography>
                                </Box>
                                <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                    <List>
                                        {[
                                            { label: 'NÜFUS İL', value: target?.NUFUSIL },
                                            { label: 'NÜFUS İLÇE', value: target?.NUFUSILCE }
                                        ].map((item, index) => (
                                            <StyledListItem 
                                                key={index}
                                                onClick={() => onCopyToClipboard(item.value, item.label)}
                                            >
                                                <ListItemText
                                                    primary={item.label}
                                                    secondary={item.value}
                                                    primaryTypographyProps={{
                                                        variant: 'subtitle2',
                                                        color: 'textSecondary'
                                                    }}
                                                    secondaryTypographyProps={{
                                                        variant: 'body1',
                                                        color: 'textPrimary'
                                                    }}
                                                />
                                                <ContentCopy color="action" fontSize="small" />
                                            </StyledListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </DetailSection>

                            {/* Address List */}
                            {locations?.length > 0 && (
                                <DetailSection>
                                    <Box className="section-title">
                                        <LocationOn />
                                        <Typography variant="h6">Kayıtlı Adresler</Typography>
                                    </Box>
                                    <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                        <List>
                                            {locations.map((location, i) => (
                                                <StyledListItem
                                                    key={i}
                                                    onClick={() => onCopyToClipboard(location?.Ikametgah, "Adres")}
                                                >
                                                    <ListItemText
                                                        primary={location?.Ikametgah}
                                                        secondary={
                                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<LocationOn />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleShowMap(location?.Ikametgah);
                                                                    }}
                                                                >
                                                                    Haritada Göster
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<ContentCopy />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onCopyToClipboard(location?.Ikametgah, "Adres");
                                                                    }}
                                                                >
                                                                    Kopyala
                                                                </Button>
                                                            </Box>
                                                        }
                                                    />
                                                </StyledListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                </DetailSection>
                            )}

                            {/* Phone Numbers */}
                            {numbers?.length > 0 && (
                                <DetailSection>
                                    <Box className="section-title">
                                        <Phone />
                                        <Typography variant="h6">Telefon Numaraları</Typography>
                                    </Box>
                                    <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                        <List>
                                            {numbers.map((number, i) => (
                                                <StyledListItem
                                                    key={i}
                                                >
                                                    <ListItemText
                                                        primary={`+90${number?.GSM}`}
                                                        secondary={
                                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<Phone />}
                                                                    href={`tel:+90${number?.GSM}`}
                                                                >
                                                                    Ara
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<Info />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onShowNotification(`Numara Bilgisi: ${number?.GSM}`, 'info');
                                                                    }}
                                                                >
                                                                    Bilgi
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<ContentCopy />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onCopyToClipboard(number?.GSM, number?.GSM);
                                                                    }}
                                                                >
                                                                    Kopyala
                                                                </Button>
                                                            </Box>
                                                        }
                                                    />
                                                </StyledListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                </DetailSection>
                            )}
                        </Grid>

                        {/* Yetkili Olduğu İşyerleri */}
                        <Grid item xs={12}>
                            <DetailSection>
                                <Box className="section-title">
                                    <Work />
                                    <Typography variant="h6">Yetkili Olduğu İşyerleri</Typography>
                                </Box>
                                <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                    <ExclusiveDatagrid
                                        title="Yetkili olduğu işyerleri"
                                        columns={[
                                            { field: 'isyeriSgkSicilNo', headerName: 'Sicil NO', width: 280 },
                                            { field: 'isyeriUnvani', headerName: 'Şirket ÜNVAN', width: 280 },
                                            { field: 'isyeriNaceKodu', headerName: 'Nace Kodu', width: 130 },
                                            { field: 'isyeriTehlikeSinifi', headerName: 'Tehlike Sınıfı', width: 130 },
                                            { field: 'yetkiTuru', headerName: 'Yetki Türü', width: 200 },
                                            { field: 'yetkililikDurumu', headerName: 'Durum', width: 140 }
                                        ]}
                                        data={(jobsauthorities ?? []).map((v,i)=>{return {id:i,...v}})}
                                        startWithActions={true}
                                        actions={[
                                            (params) => (
                                                <Tooltip title="Detayları Görüntüle">
                                                    <GridActionsCellItem
                                                        icon={<RemoveRedEye />}
                                                        onClick={() => onJobDetails(params.row)}
                                                        label="Details"
                                                    />
                                                </Tooltip>
                                            )
                                        ]}
                                    />
                                </Paper>
                            </DetailSection>
                        </Grid>

                        {/* Employment Information */}
                        <Grid item xs={12}>
                            <DetailSection>
                                <Box className="section-title">
                                    <Work />
                                    <Typography variant="h6">İş Bilgileri</Typography>
                                </Box>
                                <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                    <ExclusiveDatagrid
                                        title="Çalıştığı işyerleri"
                                        columns={[
                                            { field: 'isyeriSgkSicilNo', headerName: 'Sicil NO', width: 280 },
                                            { field: 'isyeriUnvani', headerName: 'Şirket ÜNVAN', width: 280 },
                                            { field: 'isyeriNaceKodu', headerName: 'Nace Kodu', width: 130 },
                                            { field: 'isyeriTehlikeSinifi', headerName: 'Tehlike Sınıfı', width: 130 },
                                            { field: 'iseGirisTarihi', headerName: 'Giriş Tarihi', width: 260 },
                                            { field: 'calismaDurumu', headerName: 'Durum', width: 140 }
                                        ]}
                                        data={jobs}
                                        startWithActions={true}
                                        actions={[
                                            (params) => (
                                                <Tooltip title="Detayları Görüntüle">
                                                    <GridActionsCellItem
                                                        icon={<RemoveRedEye />}
                                                        onClick={() => onJobDetails(params.row)}
                                                        label="Details"
                                                    />
                                                </Tooltip>
                                            )
                                        ]}
                                    />
                                </Paper>
                            </DetailSection>
                        </Grid>
                    </Grid>
                </Container>
            </StyledDialog>

            {/* Harita Modalı */}
            <Dialog
                open={showMap}
                onClose={handleCloseMap}
                maxWidth="md"
                fullWidth
            >
                <DialogContent>
                    <Box sx={{ position: 'relative' }}>
                        <IconButton
                            onClick={handleCloseMap}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                zIndex: 1,
                                bgcolor: 'background.paper',
                                '&:hover': {
                                    bgcolor: 'background.paper',
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <LocationSearch address={selectedAddress} />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DataModal; 